import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

import ConstatService from "../../../services/smq/ConstatService";
import { getDisplayedNcStatus } from "../NC/ncStatusHelper";

const NAVY = "FF173D61";
const LIGHT_BLUE = "FFEAF1F7";
const WHITE = "FFFFFFFF";
const EMPTY_VALUE = "—";
const PAGE_SIZE = 50;

const DETAIL_EXPORTS = [
  { key: "closed", sheetName: "NC clôturées", title: "NC clôturées" },
  { key: "open", sheetName: "NC ouvertes", title: "NC ouvertes" },
  { key: "due_soon", sheetName: "Échéances proches", title: "Échéances dans les 3 prochains jours" },
  { key: "expired", sheetName: "Délais dépassés", title: "NC avec délai dépassé" },
  { key: "curative", sheetName: "Curatifs en cours", title: "Actions curatives en cours" },
  { key: "corrective", sheetName: "Correctives en cours", title: "Actions correctives en cours" },
];

const STATUS_LABELS = {
  delai_depasse: "Délai dépassé",
  done: "Clôturée",
  done_after_reopen: "Clôturée après réouverture",
  in_progress: "En cours",
  open: "Ouverte",
  reopen: "Rouverte",
};

const safeValue = (value) => {
  if (value === null || value === undefined || value === "") return EMPTY_VALUE;
  return value;
};

const formatDate = (value) => {
  const dateValue = String(value || "").slice(0, 10);
  if (!dateValue) return EMPTY_VALUE;
  const date = new Date(`${dateValue}T00:00:00`);
  return Number.isNaN(date.getTime())
    ? EMPTY_VALUE
    : date.toLocaleDateString("fr-FR");
};

const statusLabel = (row) => {
  const status = row.statut === "done" && row.has_reopened_child
    ? "done_after_reopen"
    : getDisplayedNcStatus(row.statut, row.delai);
  return STATUS_LABELS[status] || safeValue(row.statut);
};

const ncColumns = [
  { header: "Référence NC", key: "reference", width: 25, value: (row) => safeValue(row.reference) },
  { header: "Date", key: "date", width: 14, value: (row) => formatDate(row.constat_date) },
  { header: "Processus", key: "processus", width: 24, value: (row) => safeValue(row.processus) },
  { header: "Direction", key: "direction", width: 16, value: (row) => safeValue(row.direction_code) },
  { header: "Description du constat", key: "description", width: 48, value: (row) => safeValue(row.description_constat) },
  { header: "Criticité", key: "criticite", width: 16, value: (row) => safeValue(row.criticite) },
  { header: "Échéance", key: "echeance", width: 14, value: (row) => formatDate(row.delai) },
  { header: "Statut", key: "statut", width: 28, value: statusLabel },
];

const curativeColumns = [
  { header: "Référence NC", key: "reference", width: 25, value: (row) => safeValue(row.reference) },
  { header: "Processus", key: "processus", width: 24, value: (row) => safeValue(row.processus) },
  { header: "Direction", key: "direction", width: 16, value: (row) => safeValue(row.direction_code) },
  { header: "Action curative", key: "description", width: 48, value: (row) => safeValue(row.description) },
  { header: "Responsable", key: "responsable", width: 28, value: (row) => safeValue(row.responsable_name) },
  { header: "Date de réalisation", key: "date", width: 20, value: (row) => formatDate(row.date_realisation) },
  { header: "Statut", key: "statut", width: 18, value: (row) => row.statut === "en_cours" ? "En cours" : "À faire" },
];

const correctiveColumns = [
  { header: "Référence NC", key: "reference", width: 25, value: (row) => safeValue(row.reference) },
  { header: "Processus", key: "processus", width: 24, value: (row) => safeValue(row.processus) },
  { header: "Direction", key: "direction", width: 16, value: (row) => safeValue(row.direction_code) },
  { header: "Action corrective", key: "action", width: 42, value: (row) => safeValue(row.action_corrective) },
  { header: "Plan d'action", key: "plan", width: 42, value: (row) => safeValue(row.plan_action) },
  { header: "Responsable(s)", key: "responsables", width: 30, value: (row) => safeValue(row.responsable_name) },
  { header: "Avancement", key: "avancement", width: 15, value: (row) => `${Number(row.avancement) || 0} %` },
  { header: "Début", key: "debut", width: 14, value: (row) => formatDate(row.date_debut) },
  { header: "Fin prévue", key: "fin", width: 14, value: (row) => formatDate(row.date_fin) },
  { header: "Délai", key: "delai", width: 12, value: (row) => Number.isFinite(Number(row.delai_jours)) ? `${Number(row.delai_jours)} j` : EMPTY_VALUE },
];

const styleTitle = (worksheet, title, columnCount) => {
  worksheet.mergeCells(1, 1, 1, columnCount);
  const cell = worksheet.getCell(1, 1);
  cell.value = title;
  cell.font = { bold: true, color: { argb: WHITE }, size: 16 };
  cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: NAVY } };
  cell.alignment = { horizontal: "center", vertical: "middle" };
  worksheet.getRow(1).height = 28;
};

const styleHeader = (row) => {
  row.height = 24;
  row.eachCell((cell) => {
    cell.font = { bold: true, color: { argb: WHITE } };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: NAVY } };
    cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
    cell.border = {
      bottom: { color: { argb: "FF9FB4C8" }, style: "thin" },
      left: { color: { argb: "FF9FB4C8" }, style: "thin" },
      right: { color: { argb: "FF9FB4C8" }, style: "thin" },
      top: { color: { argb: "FF9FB4C8" }, style: "thin" },
    };
  });
};

const addTableSheet = (workbook, { columns, rows, sheetName, title }) => {
  const worksheet = workbook.addWorksheet(sheetName);
  worksheet.columns = columns.map(({ key, width }) => ({ key, width }));
  styleTitle(worksheet, title, columns.length);
  const headerRow = worksheet.getRow(3);
  columns.forEach((column, index) => {
    headerRow.getCell(index + 1).value = column.header;
  });
  styleHeader(headerRow);

  rows.forEach((sourceRow, index) => {
    const row = worksheet.addRow(
      columns.reduce((values, column) => ({ ...values, [column.key]: column.value(sourceRow) }), {})
    );
    row.alignment = { vertical: "top", wrapText: true };
    row.height = 30;
    if (index % 2 === 1) {
      row.eachCell((cell) => {
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF5F8FB" } };
      });
    }
  });

  if (!rows.length) {
    worksheet.mergeCells(4, 1, 4, columns.length);
    worksheet.getCell(4, 1).value = "Aucune donnée pour les filtres sélectionnés.";
    worksheet.getCell(4, 1).alignment = { horizontal: "center" };
  }

  worksheet.autoFilter = { from: { row: 3, column: 1 }, to: { row: 3, column: columns.length } };
  worksheet.views = [{ state: "frozen", ySplit: 3 }];
};

const addSummarySheet = (workbook, { chartImages, filterLabels, summary }) => {
  const worksheet = workbook.addWorksheet("Synthèse", { views: [{ showGridLines: false }] });
  worksheet.columns = Array.from({ length: 12 }, () => ({ width: 15 }));
  styleTitle(worksheet, "Tableau de bord SMQ / NC", 12);
  worksheet.mergeCells("A2:L2");
  worksheet.getCell("A2").value = `Export généré le ${new Date().toLocaleString("fr-FR")}`;
  worksheet.getCell("A2").alignment = { horizontal: "center" };

  worksheet.getCell("A4").value = "Filtres appliqués";
  worksheet.getCell("A4").font = { bold: true, color: { argb: NAVY } };
  [
    ["Processus", filterLabels.processus],
    ["Direction", filterLabels.direction],
    ["Du", filterLabels.date_debut],
    ["Au", filterLabels.date_fin],
  ].forEach(([label, value], index) => {
    const row = 5 + index;
    worksheet.getCell(row, 1).value = label;
    worksheet.getCell(row, 1).font = { bold: true };
    worksheet.getCell(row, 2).value = safeValue(value);
    worksheet.mergeCells(row, 2, row, 4);
  });

  const stats = summary.stats || {};
  const kpis = [
    ["NC enregistrées", stats.total],
    ["NC clôturées", stats.closed],
    ["NC ouvertes", stats.open],
    ["NC en cours", stats.in_progress],
    ["Échéances proches", stats.due_soon],
    ["Délais dépassés", stats.expired],
    ["Curatifs en cours", stats.curatifs_in_progress],
    ["Correctives en cours", stats.correctives_in_progress],
  ];
  worksheet.getCell("F4").value = "Indicateurs";
  worksheet.getCell("F4").font = { bold: true, color: { argb: NAVY } };
  kpis.forEach(([label, value], index) => {
    const row = 5 + index;
    worksheet.getCell(row, 6).value = label;
    worksheet.getCell(row, 7).value = Number(value) || 0;
    worksheet.getCell(row, 6).fill = { type: "pattern", pattern: "solid", fgColor: { argb: LIGHT_BLUE } };
    worksheet.getCell(row, 6).font = { bold: true };
  });

  let nextRow = 15;
  if (chartImages.status) {
    const imageId = workbook.addImage({ base64: chartImages.status, extension: "png" });
    worksheet.addImage(imageId, { tl: { col: 0, row: nextRow - 1 }, ext: { width: 520, height: 270 } });
  }
  if (chartImages.process) {
    const imageId = workbook.addImage({ base64: chartImages.process, extension: "png" });
    worksheet.addImage(imageId, { tl: { col: 6, row: nextRow - 1 }, ext: { width: 760, height: 270 } });
  }

  nextRow = 32;
  worksheet.getCell(nextRow, 1).value = "Répartition par statut — données sources";
  worksheet.getCell(nextRow, 1).font = { bold: true, color: { argb: NAVY } };
  worksheet.getCell(nextRow + 1, 1).value = "Statut";
  worksheet.getCell(nextRow + 1, 2).value = "Total";
  styleHeader(worksheet.getRow(nextRow + 1));
  (summary.status_distribution || []).forEach((item) => {
    worksheet.addRow([safeValue(item.label), Number(item.total) || 0]);
  });

  const processStartRow = 32;
  worksheet.getCell(processStartRow, 6).value = "NC par processus — données sources";
  worksheet.getCell(processStartRow, 6).font = { bold: true, color: { argb: NAVY } };
  worksheet.getCell(processStartRow + 1, 6).value = "Processus";
  worksheet.getCell(processStartRow + 1, 7).value = "Total";
  [worksheet.getCell(processStartRow + 1, 6), worksheet.getCell(processStartRow + 1, 7)].forEach((cell) => {
    cell.font = { bold: true, color: { argb: WHITE } };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: NAVY } };
  });
  (summary.process_distribution || []).forEach((item, index) => {
    worksheet.getCell(processStartRow + 2 + index, 6).value = safeValue(item.process);
    worksheet.getCell(processStartRow + 2 + index, 7).value = Number(item.total) || 0;
  });
};

const loadAllRows = async (detailType, filters) => {
  const rows = [];
  let page = 1;
  let totalPages = 1;

  do {
    const response = await ConstatService.getDashboardDetails(detailType, {
      ...filters,
      page,
      page_size: PAGE_SIZE,
    });
    const data = response?.data;
    if (!Array.isArray(data?.rows) || !data?.pagination) {
      throw new Error("Invalid dashboard detail response");
    }
    rows.push(...data.rows);
    totalPages = Math.max(1, Number(data.pagination.total_pages) || 0);
    page += 1;
  } while (page <= totalPages);

  return rows;
};

export const fetchDashboardExportDetails = async (filters) => {
  const groups = await Promise.all(
    DETAIL_EXPORTS.map(async (config) => ({
      ...config,
      rows: await loadAllRows(config.key, filters),
    }))
  );
  return groups;
};

export const saveDashboardWorkbook = async ({
  chartImages,
  detailGroups,
  filterLabels,
  recentRows,
  summary,
}) => {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = "FID — SMQ / NC";
  workbook.created = new Date();

  addSummarySheet(workbook, { chartImages, filterLabels, summary });
  detailGroups.forEach((group) => {
    const columns = group.key === "curative"
      ? curativeColumns
      : group.key === "corrective"
        ? correctiveColumns
        : ncColumns;
    addTableSheet(workbook, { ...group, columns });
  });
  addTableSheet(workbook, {
    columns: ncColumns,
    rows: recentRows,
    sheetName: "Dernières NC",
    title: "10 dernières NC enregistrées",
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const now = new Date();
  const timestamp = [
    now.getFullYear(),
    String(now.getMonth() + 1).padStart(2, "0"),
    String(now.getDate()).padStart(2, "0"),
    String(now.getHours()).padStart(2, "0"),
    String(now.getMinutes()).padStart(2, "0"),
  ].join("-");
  saveAs(
    new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
    `FID_Tableau_de_bord_NC_${timestamp}.xlsx`
  );
};

